import { useState } from 'react'
import { useFetchOrderSources } from '@/hooks/features/order-source'
import { OrderModel } from '@/http/models/Order/Order'
import { OrderSourceModel as OrderSourceApi } from '@/http/models/OrderSource'
import { toast } from 'sonner'
import { OrderSourceModel } from '@/types'
import { SearchOrCreateCombobox } from './SearchOrCreateCombobox'

interface OrderSourceSelectProps {
    orderId: string
    currentSourceId: string | null
    /** The order's expanded source name, for when it isn't in the first page of sources. */
    currentLabel?: string
    onUpdate?: () => void
    sources?: OrderSourceModel.OrderSource[]
}

export const OrderSourceSelect = ({ orderId, currentSourceId, currentLabel, onUpdate, sources: providedSources }: OrderSourceSelectProps) => {
    const [loading, setLoading] = useState(false)
    const [created, setCreated] = useState<OrderSourceModel.OrderSource | null>(null)
    const { data: fetchedSources } = useFetchOrderSources({
        shouldFetchList: !providedSources,
    })

    const listed = providedSources || fetchedSources
    const sources = created ? [created, ...listed.filter(s => s._id !== created._id)] : listed

    const handleUpdate = async (sourceId: string) => {
        setLoading(true)
        const response = await OrderModel.updateSource({
            params: { id: orderId },
            body: { order_source_id: sourceId },
        })

        if (response.success) {
            toast.success('Source updated successfully')
            onUpdate?.()
        } else {
            toast.error('Failed to update source')
        }
        setLoading(false)
    }

    const searchSources = async (search: string) => {
        const response = await OrderSourceApi.getList({ query: { search, page: 1, length: 10 } })
        return response.success ? (response.data?.result ?? []) : []
    }

    const handleCreate = async (name: string) => {
        setLoading(true)
        const response = await OrderSourceApi.create({ body: { name } })
        const source: OrderSourceModel.OrderSource | undefined = response.data?.result

        if (response.success && source?._id) {
            toast.success(`Source "${name}" created`)
            setCreated(source)
            await handleUpdate(source._id)
        } else {
            toast.error(response.error?.data?.message || 'Failed to create source')
            setLoading(false)
        }
    }

    return (
        <SearchOrCreateCombobox
            items={sources}
            currentId={currentSourceId}
            currentLabel={currentLabel}
            getId={(source) => source._id}
            getLabel={(source) => source.name}
            search={searchSources}
            onSelect={(source) => handleUpdate(source._id)}
            onCreate={handleCreate}
            placeholder="Select source"
            searchPlaceholder="Search or create a source"
            emptyText="No source found."
            busy={loading}
        />
    )
}
